import React, { useCallback, useEffect, useState, useRef } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useVisitorGeo } from '../hooks/useVisitorGeo';
import { DEFAULT_CITIES } from '../utils/geo';
// Always show the ebook cover, regardless of what the scanner finds
const EBOOK_IMAGE = 'https://cdn.magicpatterns.com/uploads/rdHi3rAEoBrUtDvyQN1xfL/mockup_cenas_antiinf15.png';
// 50/50 split between the two editions
const EDITIONS = ['Pack Completo Antiinflamatorio', 'Cenas Antiinflamatorias'];
const NAMES = ['María', 'Sofía', 'Valentina', 'Lucía', 'Camila', 'Martina', 'Florencia', 'Carolina', 'Gabriela', 'Paula', 'Daniela', 'Romina', 'Andrea', 'Verónica', 'Mariana', 'Agustina', 'Julieta', 'Cecilia', 'Natalia', 'Silvina', 'Patricia', 'Mónica', 'Laura', 'Adriana', 'Claudia'];
interface ToastData {
  id: number;
  name: string;
  city: string;
  edition: string;
  agoText: string;
}
function randomFrom<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}
function randomAgoText(): string {
  // A few seconds up to 9 minutes
  const totalSeconds = Math.floor(Math.random() * (9 * 60 - 5)) + 5;
  if (totalSeconds < 60) {
    return `hace ${totalSeconds} segundos`;
  }
  const minutes = Math.round(totalSeconds / 60);
  return `hace ${minutes} ${minutes === 1 ? 'minuto' : 'minutos'}`;
}
export function RecentPurchaseToast() {
  const prefersReducedMotion = useReducedMotion();
  const geo = useVisitorGeo();
  const [toast, setToast] = useState<ToastData | null>(null);
  const citiesRef = useRef<string[]>(geo.cities);
  const nameHistoryRef = useRef<string[]>([]);
  const idRef = useRef(0);
  const startedRef = useRef(false);
  const timeoutsRef = useRef<number[]>([]);
  // Keep the city pool in sync with the resolved country
  useEffect(() => {
    citiesRef.current = geo.cities && geo.cities.length > 0 ? geo.cities : DEFAULT_CITIES;
  }, [geo.cities]);
  const pickName = useCallback(() => {
    const history = nameHistoryRef.current;
    const available = NAMES.filter(n => !history.includes(n));
    const pool = available.length > 0 ? available : NAMES;
    const name = randomFrom(pool);
    history.push(name);
    if (history.length > 10) history.shift();
    return name;
  }, []);
  const showOne = useCallback(() => {
    const data: ToastData = {
      id: ++idRef.current,
      name: pickName(),
      city: randomFrom(citiesRef.current),
      edition: randomFrom(EDITIONS),
      agoText: randomAgoText()
    };
    setToast(data);
    // Visible 4–5s, then dismiss
    const visibleMs = 4000 + Math.random() * 1000;
    const hideTimeout = window.setTimeout(() => {
      setToast(null);
      // Schedule next after random 8–15s gap
      const gapMs = 8000 + Math.random() * 7000;
      const nextTimeout = window.setTimeout(showOne, gapMs);
      timeoutsRef.current.push(nextTimeout);
    }, visibleMs);
    timeoutsRef.current.push(hideTimeout);
  }, [pickName]);
  const start = useCallback(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    // First toast after 3s once the reading threshold is reached
    const firstTimeout = window.setTimeout(showOne, 3000);
    timeoutsRef.current.push(firstTimeout);
  }, [showOne]);
  // Hold the notifications back until the visitor has read at least half the
  // page, or has arrived at the offer stack — whichever happens first.
  useEffect(() => {
    let observer: IntersectionObserver | null = null;
    const cleanupScroll = () => {
      window.removeEventListener('scroll', onScroll);
    };
    function trigger() {
      if (startedRef.current) return;
      start();
      observer?.disconnect();
      cleanupScroll();
    }
    function onScroll() {
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollable <= 0) return;
      const progress = window.scrollY / scrollable;
      if (progress >= 0.5) trigger();
    }
    const target = document.getElementById('offer-section');
    if (target) {
      observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) trigger();
        });
      }, {
        threshold: 0.2
      });
      observer.observe(target);
    }
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    onScroll();
    return () => {
      observer?.disconnect();
      cleanupScroll();
      timeoutsRef.current.forEach(t => window.clearTimeout(t));
      timeoutsRef.current = [];
    };
  }, [start]);
  const enterAnim = {
    opacity: 1,
    y: 0
  };
  const initialAnim = prefersReducedMotion ? {
    opacity: 0,
    y: 0
  } : {
    opacity: 0,
    y: 24
  };
  const exitAnim = prefersReducedMotion ? {
    opacity: 0,
    y: 0
  } : {
    opacity: 0,
    y: 24
  };
  return <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[90] w-full max-w-sm px-4 pointer-events-none" aria-live="polite" data-id="element-472" data-mp="32gt9d5iox47h">
      <AnimatePresence data-id="element-473" data-mp="lvo819wpsc4q">
        {toast && <motion.div key={toast.id} initial={initialAnim} animate={enterAnim} exit={exitAnim} transition={{
        duration: prefersReducedMotion ? 0.15 : 0.25,
        ease: [0.23, 1, 0.32, 1]
      }} className="pointer-events-auto flex items-center gap-3 bg-white rounded-xl shadow-warm border border-forest/10 p-3 pr-4" role="status" data-id="element-474" data-mp="lwz7ixj4mxq1">
            <div className="w-12 h-12 rounded-lg overflow-hidden shrink-0 bg-cream border border-forest/10 flex items-center justify-center" data-id="element-475" data-mp="1jj6daar62w3z">
              <img src={EBOOK_IMAGE} alt="Cenas Antiinflamatorias" className="w-full h-full object-cover" data-id="element-476" data-mp="1elmxv7iqgdag" />
            </div>
            <div className="min-w-0 flex-1" data-id="element-477" data-mp="3gcqcs8znoe0t">
              <p className="text-sm text-dark leading-snug" data-id="element-478" data-mp="3ge1f04dgddpt">
                <span className="font-bold" data-id="element-479" data-mp="1k9vbqi1i66x0">{toast.name}</span> en{' '}
                <span className="font-medium" data-id="element-480" data-mp="1p74zlt98uaiq">{toast.city}</span> acaba de
                comprar{' '}
                <span className="font-semibold text-forest" data-id="element-481" data-mp="1t8qgdpowf8y7">
                  {toast.edition}
                </span>
              </p>
              <p className="text-xs text-dark/50 mt-0.5 flex items-center gap-1.5" data-id="element-482" data-mp="1y9wbfu6178aw">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-success animate-pulse" data-id="element-483" data-mp="236mrwilr05wu" />
                {toast.agoText}
              </p>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}